import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { createPaymentAction } from "../../store/actions/paymentsActions";
import { paymentRequestCancelled } from "../../store/reducers/paymentsReducers";

import { trackEvent } from "../../services/analytics";

export const CANCEL_BUTTON_DELAY_MS = 10000;
export const REQUEST_TIMEOUT_MS = 20000;

// Shared by EditDoc.jsx and EditManyDocs.jsx: creating a payment (PbReader
// preflight + QR request) can stall — a slow network, a backed-up preview
// worker — with nothing but a spinner and no way out for a kiosk user. This
// adds a hard timeout and an earlier manual "Отменить" escape hatch.
export function usePaymentRequest() {
    const dispatch = useDispatch();
    const attemptRef = useRef(0);
    const reasonRef = useRef(null);
    const [showCancel, setShowCancel] = useState(false);

    const cancel = useCallback(
        (reason = "manual") => {
            trackEvent("payment_request_cancelled", { reason });
            attemptRef.current++;
            reasonRef.current = reason;
            setShowCancel(false);
            dispatch(paymentRequestCancelled());
        },
        [dispatch],
    );

    const request = useCallback(
        async (paymentData) => {
            trackEvent("payment_request", { source: "ui" });
            const attempt = ++attemptRef.current;
            setShowCancel(false);
            const cancelButtonTimer = setTimeout(() => {
                if (attemptRef.current === attempt) setShowCancel(true);
            }, CANCEL_BUTTON_DELAY_MS);
            const timeoutTimer = setTimeout(() => {
                if (attemptRef.current === attempt) cancel("timeout");
            }, REQUEST_TIMEOUT_MS);

            const resultAction = await dispatch(createPaymentAction(paymentData));

            clearTimeout(cancelButtonTimer);
            clearTimeout(timeoutTimer);
            // A cancel/timeout already fired: the reducer reset loadingPayment
            // and the user moved on. This late result must not navigate or
            // surface a toast for a request the user gave up waiting on.
            if (attemptRef.current !== attempt) return { cancelled: true, reason: reasonRef.current };
            setShowCancel(false);
            trackEvent("payment_request_result", {
                status: resultAction.meta.requestStatus,
                ...(resultAction.payload?.order_id != null ? { order_id: String(resultAction.payload.order_id) } : {}),
                ...(["kaspi_qr", "smartvend", "admin_free"].includes(resultAction.payload?.provider) ? { provider: resultAction.payload.provider } : {}),
            });
            return { cancelled: false, resultAction };
        },
        [dispatch, cancel],
    );

    return { request, cancel, showCancel };
}
